import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  MessageCircle, 
  MoreVertical,
  Heart as HeartFilled,
  Edit,
  Trash2,
  Flag,
  ChevronLeft, 
  ChevronRight,
  FileText,
  Clock,
  Tag,
  X
} from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import communityAPI from '../../services/community.api';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import CommentsSection from './CommentsSection';
import useReportStore from '../../store/reportStore';
import EditPostModal from './EditPostModal';

const getMediaType = (url) => {
  if (!url) return 'unknown';
  const ext = url.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return 'video';
  if (['pdf', 'doc', 'docx'].includes(ext)) return 'document';
  return 'other';
};

const PostCard = ({ post, onPostDeleted }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { toggleLike, deletePost } = useCommunityStore();
  const { openReportModal } = useReportStore();
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showCommentModal) {
        setShowCommentModal(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showCommentModal]);

  const getAllMediaUrls = () => {
    if (post.metadata?.mediaUrls && Array.isArray(post.metadata.mediaUrls) && post.metadata.mediaUrls.length > 0) {
      return post.metadata.mediaUrls;
    }
    if (post.mediaUrl) {
      return [post.mediaUrl];
    }
    return [];
  };

  const mediaUrls = getAllMediaUrls();
  const hasMultipleMedia = mediaUrls.length > 1;
  const currentMediaUrl = mediaUrls[currentMediaIndex] || null;
  const currentMediaType = currentMediaUrl ? getMediaType(currentMediaUrl) : null;

  const handleLike = async () => {
    try {
      await toggleLike(post.id);
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await deletePost(post.id);
      setShowOptions(false);
      if (onPostDeleted) onPostDeleted();
      toast.success('Post deleted successfully!');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete post');
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
    setShowOptions(false);
  };

  const handleReport = () => {
    openReportModal({
      type: 'post',
      id: post.id,
      data: {
        content: post.content,
        author: post.user
      }
    });
    setShowOptions(false);
  };

  const handleUserClick = () => navigate(`/community/profile/${post.user.id}`);
  const handlePostClick = () => navigate(`/community/post/${post.id}`);
  const handlePostUpdated = () => toast.success('Post updated successfully!');

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Just now';
    }
  };

  const renderContentWithMentions = (content) => {
    if (!content) return null;
    
    // Safely check if metadata and mentions exist
    const mentions = post.metadata?.mentions;
    
    if (mentions && Array.isArray(mentions) && mentions.length > 0) {
      const parts = [];
      let lastIndex = 0;
      
      // Safely build mention patterns, filtering out any undefined or empty displayNames
      const mentionPatterns = mentions
        .filter(m => m && m.displayName)
        .map(m => {
          // Escape special regex characters in the display name
          const escapedName = m.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return `@${escapedName}`;
        });
      
      // If no valid mention patterns, return the content as plain text
      if (mentionPatterns.length === 0) {
        return <span className="text-xs">{content}</span>;
      }
      
      const mentionRegex = new RegExp(`(${mentionPatterns.join('|')})`, 'g');
      let match;
      
      while ((match = mentionRegex.exec(content)) !== null) {
        if (match.index > lastIndex) {
          parts.push(
            <span key={`text-${lastIndex}`} className="text-xs">
              {content.substring(lastIndex, match.index)}
            </span>
          );
        }
        
        // Find the mention data for this match
        const mentionData = mentions.find(m => m && m.displayName && `@${m.displayName}` === match[0]);
        
        if (mentionData && mentionData.userId) {
          parts.push(
            <span 
              key={`mention-${match.index}`} 
              className="text-primary-600 font-medium hover:text-primary-800 cursor-pointer text-xs"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/community/profile/${mentionData.userId}`);
              }}
            >
              {match[0]}
            </span>
          );
        } else {
          parts.push(
            <span key={`mention-${match.index}`} className="text-primary-600 font-medium text-xs">
              {match[0]}
            </span>
          );
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < content.length) {
        parts.push(
          <span key={`text-${lastIndex}`} className="text-xs">
            {content.substring(lastIndex)}
          </span>
        );
      }
      
      return parts;
    }
    
    // Fallback: handle plain text with @mentions using regex
    const mentionRegex = /(@\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = mentionRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} className="text-xs">
            {content.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      const mentionText = match[0];
      parts.push(
        <span 
          key={`mention-${match.index}`} 
          className="text-primary-600 font-medium hover:text-primary-800 cursor-pointer text-xs"
          onClick={async (e) => {
            e.stopPropagation();
            const username = mentionText.substring(1);
            try {
              const response = await communityAPI.searchUsers({ query: username, limit: 1 });
              if (response.data.users && response.data.users.length > 0) {
                navigate(`/community/profile/${response.data.users[0].id}`);
              } else {
                toast.error(`User "${username}" not found`);
              }
            } catch (error) {
              console.error('Error searching for user:', error);
              toast.error('Error finding user');
            }
          }}
        >
          {mentionText}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < content.length) {
      parts.push(
        <span key={`text-${lastIndex}`} className="text-xs">
          {content.substring(lastIndex)}
        </span>
      );
    }
    
    return parts;
  };

  const renderMedia = () => {
    if (!currentMediaUrl) return null;
    
    switch (currentMediaType) {
      case 'image':
        return (
          <img 
            src={currentMediaUrl} 
            alt={`Media ${currentMediaIndex + 1}`}
            className="w-full h-auto max-h-64 object-cover rounded-md"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="h-24 flex items-center justify-center bg-gray-100 rounded-md"><span class="text-gray-500 text-xs">Image failed to load</span></div>`;
            }}
          />
        );
      case 'video':
        return (
          <video
            src={currentMediaUrl}
            controls
            className="w-full h-auto max-h-64 rounded-md"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="h-24 flex items-center justify-center bg-gray-100 rounded-md"><span class="text-gray-500 text-xs">Video failed to load</span></div>`;
            }}
          />
        );
      default:
        return (
          <div className="h-20 flex items-center justify-center bg-gray-100 rounded-md">
            <FileText className="h-5 w-5 text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">
              {currentMediaType === 'document' ? 'Document' : 'File'}
            </span>
          </div>
        );
    }
  };

  return (
    <>
      <div 
        id={`post-${post.id}`}
        className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30 mb-2 hover:shadow-neomorph-hover transition-all"
      >
        {/* Post Header - Compact */}
        <div className="flex items-start justify-between">
          <button
            onClick={handleUserClick}
            className="flex items-center space-x-2 hover:opacity-80 text-left flex-1 group"
          >
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center flex-shrink-0">
              {post.user.avatar ? (
                <img 
                  src={post.user.avatar} 
                  alt={post.user.firstName}
                  className="h-7 w-7 rounded-lg object-cover"
                />
              ) : (
                <span className="text-white font-medium text-xs">
                  {post.user.firstName[0]}{post.user.lastName[0]}
                </span>
              )}
            </div>
            <div>
              <h4 className="font-medium text-gray-800 text-xs group-hover:text-primary-600 transition-colors">
                {post.user.firstName} {post.user.lastName}
              </h4>
              <p className="text-xs text-gray-500 flex items-center">
                <Clock className="h-2.5 w-2.5 mr-1" />
                {formatDate(post.createdAt)}
              </p>
            </div>
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="p-1 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
            >
              <MoreVertical className="h-3 w-3 text-gray-500" />
            </button>
            
            {showOptions && (
              <div className="absolute right-0 mt-2 w-44 bg-[#E8ECF5] rounded-xl shadow-neomorph border border-primary-100/30 z-10 overflow-hidden">
                {post.user.id === user?.id ? (
                  <>
                    <button onClick={handleEdit} className="flex items-center w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-white transition-all">
                      <div className="p-1 bg-blue-50 rounded-lg mr-2"><Edit className="h-3 w-3 text-blue-600" /></div>Edit Post
                    </button>
                    <button onClick={handleDelete} className="flex items-center w-full px-3 py-1.5 text-xs text-red-600 hover:bg-white transition-all">
                      <div className="p-1 bg-red-50 rounded-lg mr-2"><Trash2 className="h-3 w-3 text-red-600" /></div>Delete Post
                    </button>
                  </>
                ) : (
                  <button onClick={handleReport} className="flex items-center w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-white transition-all">
                    <div className="p-1 bg-yellow-50 rounded-lg mr-2"><Flag className="h-3 w-3 text-yellow-600" /></div>Report Post
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Post Content */}
        <div className="mt-2 cursor-pointer" onClick={handlePostClick}>
          <p className="text-gray-800 whitespace-pre-line text-xs leading-relaxed">
            {renderContentWithMentions(post.content)}
          </p>
          
          {mediaUrls.length > 0 && (
            <div className="mt-2 rounded-lg overflow-hidden relative">
              <div className="relative">
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  {renderMedia()}
                </div>
                
                {hasMultipleMedia && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentMediaIndex(prev => prev === 0 ? mediaUrls.length - 1 : prev - 1);
                      }}
                      className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm p-1 rounded-md shadow-neomorph"
                    >
                      <ChevronLeft className="h-3.5 w-3.5 text-gray-700" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentMediaIndex(prev => prev === mediaUrls.length - 1 ? 0 : prev + 1);
                      }}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm p-1 rounded-md shadow-neomorph"
                    >
                      <ChevronRight className="h-3.5 w-3.5 text-gray-700" />
                    </button>
                    
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-0.5">
                      {mediaUrls.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => { e.stopPropagation(); setCurrentMediaIndex(index); }}
                          className={`h-1 rounded-full transition-all ${
                            index === currentMediaIndex ? 'w-2 bg-primary-600' : 'w-1 bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="absolute top-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-md">
                      {currentMediaIndex + 1}/{mediaUrls.length}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Post Actions */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <button
              onClick={handleLike}
              className="flex items-center space-x-1 p-1 rounded-md hover:bg-gray-50 transition-all group"
            >
              {post.likedByUser ? (
                <HeartFilled className="h-3.5 w-3.5 text-red-500 fill-current" />
              ) : (
                <Heart className="h-3.5 w-3.5 text-gray-500 group-hover:text-red-500" />
              )}
              <span className={`text-xs font-medium ${post.likedByUser ? 'text-red-500' : 'text-gray-600'}`}>
                {post._count?.likes || 0}
              </span>
            </button>
            
            <button
              onClick={() => setShowCommentModal(true)}
              className="flex items-center space-x-1 p-1 rounded-md hover:bg-gray-50 transition-all group"
            >
              <MessageCircle className="h-3.5 w-3.5 text-gray-500 group-hover:text-primary-600" />
              <span className="text-xs font-medium text-gray-600 group-hover:text-primary-600">
                {post._count?.comments || 0}
              </span>
            </button>
          </div>
        </div>

        {/* Business Tags */}
        {post.user.businessTags && post.user.businessTags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {post.user.businessTags.slice(0, 2).map((tag, index) => (
              <span key={index} className="inline-flex items-center px-1.5 py-0.5 text-[10px] bg-primary-50 text-primary-700 rounded">
                <Tag className="h-2 w-2 mr-0.5" />
                {tag}
              </span>
            ))}
            {post.user.businessTags.length > 2 && (
              <span className="text-[10px] text-gray-500 px-1.5 py-0.5">
                +{post.user.businessTags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowCommentModal(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-xl shadow-neomorph max-w-2xl w-full max-h-[80vh] overflow-hidden border border-primary-100/30" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-primary-100/50 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-5 w-5 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <MessageCircle className="h-3 w-3 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm">Comments</h3>
              </div>
              <button onClick={() => setShowCommentModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(80vh-52px)] p-3">
              <CommentsSection postId={post.id} />
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {showEditModal && (
        <EditPostModal
          post={post}
          onClose={() => setShowEditModal(false)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </>
  );
};

export default PostCard;